import { useMemo } from 'react'
import type { MetricEvent } from '@/types/apiModels'
import { DCNamespaces } from '@/utils/dataCollection/namespaces'
import { useMetricEvents } from './useMetricEvents'

// Mirrors ruuts-api's paddockLock.js getLockedPaddockIds — the single client-side source of
// truth for the Locked Paddock Determination requirement (spec: paddock-metric-lock).
// QuantificationUnit existence and DataCollectionStatement review status must never be used
// as lock criteria here either.
//
// Like the backend, this reads the paddockIds column and folds ids to lowercase — deriving from
// data.selectedPaddocks (or comparing unfolded) can diverge from the authority on legacy rows,
// showing no "locked" badge on a paddock the save will 422 on, or a spurious permanent lock.
// selectedPaddocks remains only as a fallback for rows where the column is absent.
export const extractLockedPaddockIds = (metricEvents: MetricEvent[] | undefined): string[] => {
  const lockedIds = new Set<string>()
  metricEvents?.forEach(metricEvent => {
    const paddockIds = metricEvent.paddockIds ?? metricEvent.data?.selectedPaddocks
    paddockIds?.forEach(paddockId => lockedIds.add(String(paddockId).toLowerCase()))
  })
  return Array.from(lockedIds)
}

export const useLockedPaddocks = ({
  farmId,
  year,
}: {
  farmId: string | undefined
  year: number | string | undefined
}) => {
  const {
    data: metricEvents,
    isLoading,
    isError,
    error,
  } = useMetricEvents({
    farmId,
    namespace: DCNamespaces.paddocksUsage,
    year,
  })

  const lockedPaddockIds = useMemo(() => extractLockedPaddockIds(metricEvents), [metricEvents])

  return { lockedPaddockIds, isLoading, isError, error }
}
